import {
  DataGridPro,
  type DataGridProProps,
  type GridCallbackDetails,
  GridCellEditStopReasons,
  type GridFilterModel,
  type GridRenderCellParams,
  type GridRowId,
  type GridRowModel,
  type GridValidRowModel,
  type MuiEvent,
  getGridBooleanOperators,
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import _ from 'lodash';
import { type FC, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '@/store/hooks';
import { selectGridState, updateGridState } from '@/store/reducers/gridsSlice';
import { dataGridCol, gridBooleanCell } from './common';
import { CustomGridFilterPanel } from './CustomeGridFilterPanel';
import { GridWrapper } from './grid.styles';

import { GridColActions } from './GridColActions';
import { GridPagination } from './GridPagination';

export interface IPhoenixGridActions {
  onSave?(row: GridRowModel): Promise<void> | void;
  onCopy?(row: GridRowModel): Promise<void> | void;
  onEdit?(row: GridRowModel): Promise<void> | void;
  onDelete?(row: GridRowModel): Promise<void> | void;
  onCancel?(row: GridRowModel): Promise<void> | void;
}

export interface IPhoenixGridProps extends DataGridProProps {
  name?: string;
  height?: string;
  strict?: boolean;
  canNotEdit?: boolean;
  canNotCopy?: boolean;
  pinnedLeft?: string[];
  pinnedRight?: string[];
  actions?: IPhoenixGridActions;
  withoutConfirmation?: boolean;
  rowActionsSlot?: FC<GridRowModel>;
  editableRowId?: string | null;
  isFullHeightColumn?: boolean;
  hideFooter?: boolean;
  isFlexible?: boolean;
  autoRowHeight?: boolean;
  disablePointer?: boolean;
  gridId?: string;
  gridBorderColor?: string;
  gridHighlightColor?: string;
  paginationIconColor?: string;
  rowsPerPageOptions?: number[];
}

interface IGridRowContext {
  errors: { [key: string]: string | undefined };
  resetError: (field: string) => void;
  resetErrors: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const GridRowContext = createContext<IGridRowContext>({
  errors: {},
  resetError: () => null,
  resetErrors: () => null,
});

export const CPGrid = ({
  name = '',
  height,
  rows,
  columns,
  actions,
  pageSize = 10,
  rowsPerPageOptions,
  canNotEdit,
  canNotCopy,
  disablePointer = false,
  initialState,
  pinnedLeft,
  pinnedRight,
  withoutConfirmation,
  rowActionsSlot,
  isFullHeightColumn,
  hideFooter,
  onFilterModelChange,
  gridBorderColor,
  onPageChange,
  isFlexible = false,
  autoRowHeight = false,
  gridId,
  gridHighlightColor = '',
  paginationIconColor = '',
  ...props
}: IPhoenixGridProps) => {
  const gridInitialState = {
    filter: {
      filterModel: {
        items: [],
      },
    },
    pagination: {
      page: 0,
      pageSize: pageSize,
    },
    sorting: {
      sortModel: [],
    },
  };
  const gridName = gridId || name;
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const savedState = useAppSelector(selectGridState(gridName));
  const ref = props?.apiRef || apiRef;
  const [editableRowError, setEditableRowErrors] = useState({});
  const [editableRowId, setEditableRowId] = useState<GridRowId | null>(props?.editableRowId || null);
  const [page, setPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [forceUpdate, setForceUpdate] = useState(0);
  
  const handleResetError = useCallback((field: string) => {
    setEditableRowErrors((prevState) => ({ ...prevState, [field]: undefined }));
  }, []);
  
  const handleResetErrors = useCallback(() => setEditableRowErrors({}), []);

  const updatedColumns = useMemo(() => (
    columns.map((col) => {
      if (col.type === 'number') {
        return {
          ...col,
          filterOperators: getGridNumericOperators().filter(
            (operator) => operator.value === '>' || operator.value === '<' || operator.value === '=' || operator.value === '<=' || operator.value === '>=',
          ),
        };
      } else if (col.type === 'date' || col.type === 'dateTime') {
        return {
          ...col,
          filterOperators: getGridDateOperators().filter(
            (operator) =>  operator.value === 'is' || operator.value === 'after' || operator.value === 'before' || operator.value === 'onOrAfter' || operator.value === 'onOrBefore'
          ),
        };
      } else if (col.type === 'boolean') {
        return {
          ...col,
          filterOperators: getGridBooleanOperators(),
        };
      }

      return {
        ...col,
        filterOperators: getGridStringOperators().filter(
          (operator) => operator.value === 'contains' || operator.value === 'equals'
        ),
      };
    })
  ), [columns]);

  useEffect(() => {
    const currentState = ref.current?.exportState();

    if (savedState) {
      const { ...restSavedState } = savedState;

      ref.current?.restoreState({
        ...currentState,
        ...restSavedState,
      });
    } else {
      ref.current?.restoreState({
        ...currentState,
        ...gridInitialState,
      });
    }
  }, [gridName]);

  useEffect(() => {
    const handleZoom = () => {
      setForceUpdate(prev => prev + 1);
    };
    
    window.addEventListener('resize', handleZoom);
    return () => window.removeEventListener('resize', handleZoom);
  }, []);

  const processSaveRow = useCallback(
    async(row: GridRowModel) => {
      const response: any = await actions?.onSave?.(row);
      
      if (!response?.error) {
        ref.current.stopRowEditMode({ id: row.id, ignoreModifications: true });
        handleResetErrors();
        setEditableRowId(null);
        return row;
      }
    },
    [actions?.onSave]
  );

  const processRowUpdate = useCallback(
    async (newRow: GridValidRowModel, oldRow: GridValidRowModel) => {
        const result = await processSaveRow(newRow);
        return result ? newRow : oldRow;
    },
    [processSaveRow]
  );

  const onSaveClick = useCallback((row: GridRowModel) => {
    ref.current.stopRowEditMode({ id: row.id });
    setEditableRowId(null);
  }, [editableRowId]);

  const gridColActions = useMemo(
    () => ([
      dataGridCol('hidden', {
        hide: true,
        ...gridBooleanCell
      }),
      {
        minWidth: 105,
        field: 'options',
        headerName: 'Actions',
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams) => (
          <GridColActions
            ref={ref}
            name={name}
            params={params}
            isRowValid={true}
            canNotCopy={canNotCopy}
            canNotEdit={canNotEdit}
            rowActionsSlot={rowActionsSlot}
            setEditableRowId={setEditableRowId}
            editableRowId={editableRowId}
            pageSize={pageSize}
            onSaveClick={() => onSaveClick(params.row)}
            onCancelClick={actions?.onCancel}
            onCopyClick={actions?.onCopy}
            onEditClick={actions?.onEdit}
          />
        ),
      }]),
    [editableRowId, name, pageSize, withoutConfirmation, rowActionsSlot, actions, onSaveClick]
  );

  const handleSaveGridState = useCallback(
    (params: any) => {
      const gridState = {
        filter: {
          filterModel: params.filter.filterModel,
        },
        pagination: params.pagination,
        sorting: {
          sortModel: params.sorting.sortModel,
        },
      };
      const isStateChanged = !_.isEqual(gridState, savedState);

      if (gridName && isStateChanged) {
        dispatch(updateGridState({ gridName, gridState }));
      }
    },
    [gridName, savedState]
  );

  const handleApply = useCallback(() => {
    ref.current.hideFilterPanel();
  }, []);

  const onRowEditStop = useCallback((params: GridValidRowModel, event: MuiEvent ) => {
    if ([GridCellEditStopReasons.enterKeyDown, GridCellEditStopReasons.cellFocusOut, GridCellEditStopReasons.tabKeyDown].includes(params.reason)) {
      event.defaultMuiPrevented = true;
    }
  }, []);

  const handlePageChange = useCallback((page: number, details: GridCallbackDetails<any>) => {
    onPageChange?.(page, details);
    setPage(page);
  }, [onPageChange]);

  useEffect(() => {
    if (!rowsPerPageOptions) { 
      setCurrentPageSize(pageSize);
    } else {
      setCurrentPageSize(currentPageSize);
    }
  }, [pageSize, currentPageSize, rowsPerPageOptions]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    if (!rowsPerPageOptions) { 
      setCurrentPageSize(pageSize);
    } else {
      setCurrentPageSize(newPageSize);
    }
  }, [pageSize, rowsPerPageOptions]);

  const handleFilterModelChange = useCallback((filterModel: GridFilterModel, details: GridCallbackDetails<'filter'>) => {
    onFilterModelChange?.(filterModel, details);
    setFilterModel(filterModel);
    // Reset the pagination to the first page (page index 0)
    if (details.reason) {
      handlePageChange(0, {});
    }
  }, [onFilterModelChange, handlePageChange]);

  const handleClearAll = useCallback(() => {
    const newFilterModel = { items: [] };
    const newDetails = { reason: 'ClearFilter' } as GridCallbackDetails;

    handleFilterModelChange(newFilterModel, newDetails);
    handleApply();
  }, [handleFilterModelChange, handleApply]);
  
  const cols = useMemo(() => (actions ? [...updatedColumns, ...gridColActions] : updatedColumns), [actions, gridColActions, updatedColumns]);

  return (
    <>  
      <GridWrapper
        $height={height}
        $gridHighlightColor={gridHighlightColor}
        $enabled={!!height || !rows?.length}
        $isFullHeightColumn={isFullHeightColumn}
        $hideFooter={hideFooter}
        $isFlexible={isFlexible}
        $gridBorderColor={gridBorderColor}
        $disablePointer={disablePointer}
      >
        <GridRowContext.Provider
          value={{
            errors: editableRowError,
            resetError: handleResetError,
            resetErrors: handleResetErrors,
          }}
        >
          <DataGridPro
            key={forceUpdate}
            {...props}
            rows={rows || []}
            apiRef={ref}
            columns={cols}
            rowHeight={40}
            headerHeight={40}
            experimentalFeatures={{ newEditingApi: true }}
            processRowUpdate={processRowUpdate}
            getRowId={props?.getRowId || ((row) => row?.id)}
            rowsPerPageOptions={rowsPerPageOptions || [pageSize]}
            filterModel={filterModel}
            page={page}
            pageSize={currentPageSize}
            initialState={{
              ...initialState,
            }}
            pinnedColumns={{ 
              left: pinnedLeft ?? [], 
              right: (pinnedRight ?? []).concat(['options']) 
            }}
            {...(autoRowHeight && { getRowHeight: () => 'auto' })}
            components={{
              FilterPanel: CustomGridFilterPanel,
              Pagination: GridPagination
            }}
            componentsProps={{
              filterPanel: { handleApply, handleClearAll },
              pagination: { paginationIconBgColor: gridHighlightColor, paginationIconColor, rowsPerPageOptions: rowsPerPageOptions || [pageSize]}
            }}
            onPageSizeChange={handlePageSizeChange}
            onProcessRowUpdateError={(error) => console.error(error)}
            onFilterModelChange={handleFilterModelChange}
            onRowEditStop={onRowEditStop}
            onPageChange={handlePageChange}
            onStateChange={handleSaveGridState}
          />
        </GridRowContext.Provider>
      </GridWrapper>
    </>
  );
};
